import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  buildFacetIndex,
  matchFacets,
  deriveTextFacetSuggestions,
} from '../../util/searchSuggestions';
import {
  getRecentSearches,
  addRecentSearch,
  clearRecentSearches,
} from '../../util/recentSearches';

import { IconSearch, IconSpinner } from '../../components';
// Reuse the exact icons the rest of the Topbar search uses, so the input blends in.
import IconSearchDesktop from '../../containers/TopbarContainer/Topbar/TopbarSearchForm/IconSearchDesktop';
import CloseIcon from '../../containers/TopbarContainer/Topbar/CloseIcon';

import css from './KeywordAutocompleteInput.module.css';

const DEBOUNCE_WAIT_TIME = 300;
const MIN_QUERY_LENGTH = 2;
const FACET_LIMIT = 5;

const KEY_CODE_ARROW_UP = 38;
const KEY_CODE_ARROW_DOWN = 40;
const KEY_CODE_ENTER = 13;
const KEY_CODE_ESC = 27;

// Renders a single row's leading visual (thumbnail for listings, icon otherwise).
const RowIcon = ({ item }) => {
  if (item.kind === 'listing') {
    return item.listing.imageUrl ? (
      <img className={css.thumb} src={item.listing.imageUrl} alt="" />
    ) : (
      <span className={css.thumbPlaceholder} />
    );
  }
  return (
    <span className={css.rowIcon}>
      <IconSearch className={css.rowIconSvg} />
    </span>
  );
};

// Small trailing tag describing the type of a facet suggestion (e.g. "Brand").
const FacetTag = ({ item }) => {
  if (item.typeLabelKey) {
    return (
      <span className={css.tag}>
        <FormattedMessage id={item.typeLabelKey} />
      </span>
    );
  }
  return item.typeLabel ? <span className={css.tag}>{item.typeLabel}</span> : null;
};

const KeywordAutocompleteInput = props => {
  const {
    input,
    isMobile,
    placeholder,
    wrapperClassName,
    inputClassName,
    inputRef,
    onClear,
    suggestionListings,
    suggestionsInProgress,
    savedSearches,
    onFetchSearchSuggestions,
    onClearSearchSuggestions,
    onSelectListing,
    onSelectSearch,
  } = props;

  const config = useConfiguration();
  const intl = useIntl();

  const [hasFocus, setHasFocus] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recent, setRecent] = useState([]);
  const inputEl = useRef(null);

  const query = input.value || '';
  const trimmed = query.trim();
  const isEmptyQuery = trimmed.length === 0;

  // Facet index is derived from config, which is stable for the session.
  const facetIndex = useMemo(() => buildFacetIndex(config), [config]);
  const facetMatches = useMemo(
    () => matchFacets(facetIndex, trimmed, FACET_LIMIT),
    [facetIndex, trimmed]
  );
  // Brand/designer chips derived from the live results (free-text fields).
  const textFacets = useMemo(
    () => deriveTextFacetSuggestions(suggestionListings, trimmed),
    [suggestionListings, trimmed]
  );

  // Debounced live-listing fetch. onFetchSearchSuggestions comes from redux
  // connect and is referentially stable.
  const debouncedFetch = useMemo(
    () => debounce(onFetchSearchSuggestions, DEBOUNCE_WAIT_TIME),
    [onFetchSearchSuggestions]
  );
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // Build the flat, keyboard-navigable list of items across all sections.
  const items = [];
  if (isEmptyQuery) {
    recent.forEach(r =>
      items.push({ kind: 'recent', label: r.label, searchParams: r.searchParams })
    );
    (savedSearches || []).forEach(s => {
      if (s?.searchPhrase) {
        items.push({ kind: 'saved', label: s.searchPhrase });
      }
    });
  } else {
    facetMatches.forEach(f => items.push({ kind: 'facet', ...f }));
    textFacets.forEach(f => items.push({ kind: 'facet', ...f }));
    (suggestionListings || []).forEach(l =>
      items.push({ kind: 'listing', listing: l })
    );
    if (trimmed.length >= MIN_QUERY_LENGTH) {
      items.push({ kind: 'seeAll', label: trimmed });
    }
  }

  const recordRecent = entry => {
    setRecent(addRecentSearch(entry));
  };

  const close = () => {
    setHasFocus(false);
    setHighlightedIndex(-1);
    if (inputEl.current) {
      inputEl.current.blur();
    }
  };

  const runKeywordSearch = keyword => {
    const kw = (keyword || '').trim();
    if (!kw) {
      return;
    }
    recordRecent({ label: kw, searchParams: { keywords: kw } });
    onSelectSearch({ keywords: kw }, kw);
    close();
  };

  const selectItem = item => {
    if (!item) {
      return;
    }
    switch (item.kind) {
      case 'listing':
        onSelectListing(item.listing);
        close();
        break;
      case 'facet':
        recordRecent({ label: item.label, searchParams: item.searchParams });
        onSelectSearch(item.searchParams, item.label);
        close();
        break;
      case 'recent':
        recordRecent({ label: item.label, searchParams: item.searchParams });
        onSelectSearch(item.searchParams || { keywords: item.label }, item.label);
        close();
        break;
      case 'saved':
      case 'seeAll':
      default:
        runKeywordSearch(item.label);
        break;
    }
  };

  const handleChange = e => {
    const value = e.target.value;
    input.onChange(value);
    setHighlightedIndex(-1);
    if (value.trim().length >= MIN_QUERY_LENGTH) {
      debouncedFetch(value);
    } else {
      debouncedFetch.cancel();
      onClearSearchSuggestions();
    }
  };

  const handleFocus = () => {
    setHasFocus(true);
    setRecent(getRecentSearches());
  };

  const handleKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_DOWN) {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, items.length - 1));
    } else if (e.keyCode === KEY_CODE_ARROW_UP) {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.keyCode === KEY_CODE_ENTER) {
      e.preventDefault();
      if (highlightedIndex >= 0 && items[highlightedIndex]) {
        selectItem(items[highlightedIndex]);
      } else {
        // Free-text search fallback.
        runKeywordSearch(trimmed);
      }
    } else if (e.keyCode === KEY_CODE_ESC) {
      close();
    }
  };

  const handleClear = () => {
    input.onChange('');
    debouncedFetch.cancel();
    onClearSearchSuggestions();
    setHighlightedIndex(-1);
    if (inputEl.current) {
      inputEl.current.focus();
    }
    if (onClear) {
      onClear();
    }
  };

  const handleClearRecent = () => {
    clearRecentSearches();
    setRecent([]);
  };

  const hasValue = trimmed.length > 0;
  const showDropdown = hasFocus && items.length > 0;

  const renderRowContent = item => {
    switch (item.kind) {
      case 'listing': {
        const priceMaybe = item.listing.price
          ? formatMoney(intl, item.listing.price)
          : null;
        return (
          <>
            <RowIcon item={item} />
            <span className={css.rowLabel}>{item.listing.title}</span>
            {priceMaybe ? <span className={css.price}>{priceMaybe}</span> : null}
          </>
        );
      }
      case 'facet':
        return (
          <>
            <RowIcon item={item} />
            <span className={css.rowLabel}>{item.label}</span>
            <FacetTag item={item} />
          </>
        );
      case 'seeAll':
        return (
          <>
            <RowIcon item={item} />
            <span className={css.rowLabel}>
              <FormattedMessage
                id="TopbarSearchForm.seeAllResults"
                values={{ query: item.label }}
              />
            </span>
          </>
        );
      case 'recent':
      case 'saved':
      default:
        return (
          <>
            <RowIcon item={item} />
            <span className={css.rowLabel}>{item.label}</span>
          </>
        );
    }
  };

  // Section headers are inserted before the first item of each section.
  const sectionHeaderFor = (item, index) => {
    const prev = items[index - 1];
    const isFirstOfKind = !prev || prev.kind !== item.kind;
    if (!isFirstOfKind) {
      return null;
    }
    const headerKey = {
      facet: 'TopbarSearchForm.suggestionsCategories',
      listing: 'TopbarSearchForm.suggestionsListings',
      recent: 'TopbarSearchForm.suggestionsRecent',
      saved: 'TopbarSearchForm.suggestionsSaved',
    }[item.kind];

    if (!headerKey) {
      return null;
    }
    const showSpinner = item.kind === 'listing' && suggestionsInProgress;
    const showClearRecent = item.kind === 'recent';
    return (
      <li className={css.sectionHeader} key={`header-${item.kind}`}>
        <FormattedMessage id={headerKey} />
        {showSpinner ? <IconSpinner className={css.headerSpinner} /> : null}
        {showClearRecent ? (
          <button
            type="button"
            className={css.clearRecent}
            onMouseDown={e => {
              e.preventDefault();
              handleClearRecent();
            }}
          >
            <FormattedMessage id="TopbarSearchForm.clearRecent" />
          </button>
        ) : null}
      </li>
    );
  };

  return (
    <div className={classNames(wrapperClassName, css.root)}>
      <div className={css.inputContainer}>
        <input
          className={inputClassName}
          {...input}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={() => {
            // Delay so clicks on predictions (which preventDefault on mouseDown)
            // can register before the dropdown unmounts.
            setHasFocus(false);
          }}
          onKeyDown={handleKeyDown}
          id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
          data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
          ref={node => {
            inputEl.current = node;
            if (inputRef) {
              inputRef(node);
            }
          }}
          type="text"
          placeholder={placeholder}
          autoComplete="off"
        />
        {hasValue ? (
          <button
            className={css.clearButton}
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={handleClear}
            title="Clear search"
          >
            <CloseIcon className={css.clearIcon} />
          </button>
        ) : null}
        <button
          className={css.searchSubmit}
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={() => runKeywordSearch(trimmed)}
        >
          <span className={css.icon}>
            <IconSearchDesktop />
          </span>
        </button>
      </div>

      {showDropdown ? (
        <div
          className={classNames(css.predictions, {
            [css.predictionsMobile]: isMobile,
          })}
        >
          <ul className={css.list}>
            {items.map((item, index) => {
              const isHighlighted = index === highlightedIndex;
              return (
                <React.Fragment key={item.key || `${item.kind}-${index}`}>
                  {sectionHeaderFor(item, index)}
                  <li
                    className={classNames(css.row, {
                      [css.highlighted]: isHighlighted,
                    })}
                    data-testid={`suggestion-${item.kind}`}
                    onMouseDown={e => e.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => selectItem(item)}
                  >
                    {renderRowContent(item)}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

KeywordAutocompleteInput.defaultProps = {
  isMobile: false,
  placeholder: '',
  wrapperClassName: null,
  inputClassName: null,
  inputRef: null,
  onClear: null,
  suggestionListings: [],
  suggestionsInProgress: false,
  savedSearches: [],
};

KeywordAutocompleteInput.propTypes = {
  input: shape({
    value: string,
    onChange: func.isRequired,
  }).isRequired,
  isMobile: bool,
  placeholder: string,
  wrapperClassName: string,
  inputClassName: string,
  inputRef: func,
  onClear: func,
  suggestionListings: arrayOf(object),
  suggestionsInProgress: bool,
  savedSearches: arrayOf(object),
  onFetchSearchSuggestions: func.isRequired,
  onClearSearchSuggestions: func.isRequired,
  onSelectListing: func.isRequired,
  onSelectSearch: func.isRequired,
};

export default KeywordAutocompleteInput;
